import { useEffect, useState } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'

import { Alert } from '../Alert'
import { Button } from '../Button'
import { useAuth } from '../../contexts/AuthContext'
import { clearBusinessCaches, useTermsStatus } from '../../hooks/useTermsStatus'
import {
  hasUnconfirmedTermsAcceptance,
  isTermsConsentSignOutPending,
} from '../../lib/termsGate'
import { authStrings, commonStrings, termsStrings } from '../../constants/strings'
import { routes } from '../../constants/routes'

/**
 * 保護ルートの入口。Firebase の利用者がいるだけでは業務画面へ入れず、
 * 利用規約の同意状態（通常ログイン・直リンク・再読込・既存セッションの復元で共通）を確認する。
 * 状態を取得できないときは業務画面を表示しない（fail-closed）。
 */
export function RequireAuth() {
  const { user, loading, signOut } = useAuth()
  const queryClient = useQueryClient()
  const termsStatus = useTermsStatus()
  const location = useLocation()
  const [isSigningOut, setIsSigningOut] = useState(false)
  const businessAccessAllowed = termsStatus.data?.businessAccessAllowed

  // 業務利用が止まったら（同意必須ON・規約改定など）業務キャッシュを破棄する
  useEffect(() => {
    if (businessAccessAllowed === false) clearBusinessCaches(queryClient)
  }, [businessAccessAllowed, queryClient])

  if (loading) {
    return <p>{commonStrings.loadingEllipsis}</p>
  }

  if (!user) {
    // ログイン後の戻り先を保持する
    return (
      <Navigate
        to={routes.LOGIN}
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    )
  }

  // 同意の保存後、ログアウトが完了していない（失敗・再読込・別タブ）。または同意の送信結果が
  // 未確認（応答が失われた）。同意画面で本人の状態を確かめてから進める
  if (
    isTermsConsentSignOutPending(user.uid) ||
    hasUnconfirmedTermsAcceptance(user.uid)
  ) {
    return <Navigate to={routes.TERMS_CONSENT} replace />
  }

  if (termsStatus.data) {
    return termsStatus.data.businessAccessAllowed ? (
      <Outlet />
    ) : (
      <Navigate to={routes.TERMS_CONSENT} replace />
    )
  }

  if (termsStatus.isError) {
    return (
      <main className="grid min-h-screen place-items-center bg-surface px-5 text-ink">
        <section className="grid w-full max-w-[440px] gap-4 rounded-[32px] border border-white/70 bg-white/90 p-6 shadow-soft">
          <h1 className="text-lg font-bold">{termsStrings.gate.statusFailedTitle}</h1>
          <Alert>
            {termsStatus.error.message || termsStrings.gate.statusFailedBody}
          </Alert>
          <p className="text-sm text-muted">{termsStrings.gate.statusFailedBody}</p>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Button
              type="button"
              className="flex-1"
              onClick={() => {
                void termsStatus.refetch()
              }}
              loading={termsStatus.isFetching}
            >
              {termsStrings.gate.retry}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              loading={isSigningOut}
              onClick={async () => {
                setIsSigningOut(true)
                await signOut()
                setIsSigningOut(false)
              }}
            >
              {authStrings.signOut}
            </Button>
          </div>
        </section>
      </main>
    )
  }

  return <p>{termsStrings.gate.checking}</p>
}
